import React, { useState, useEffect, useRef } from 'react';
import './../App.css';
import OverviewCommand from './commands/OverviewCommand';
import TokenomicsCommand from './commands/TokenomicsCommand';
import RoadmapCommand from './commands/RoadmapCommand';
import WhitepaperCommand from './commands/WhitepaperCommand';
import FaqCommand from './commands/FaqCommand';
import PresaleCommand from './commands/PresaleCommand';

const Terminal = () => {
    const [output, setOutput] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const terminalRef = useRef(null);
    const canvasRef = useRef(null);

    // Matrix background effect
    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
  
      const resizeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
  
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
  
      const fontSize = 16;
      const chars = 'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン';
      const columns = canvas.width / fontSize;
      const drops = new Array(Math.floor(columns)).fill(1);
  
      const drawMatrix = () => {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.fillStyle = '#0F0';
        ctx.font = `${fontSize}px monospace`;
  
        drops.forEach((drop, i) => {
          const text = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillText(text, i * fontSize, drop * fontSize);
          
          if (drop * fontSize > canvas.height && Math.random() > 0.975) {
            drops[i] = 0;
          }
          drops[i]++;
        });
      };
  
      const interval = setInterval(drawMatrix, 33);
  
      return () => {
        clearInterval(interval);
        window.removeEventListener('resize', resizeCanvas);
      };
    }, []);

    // Auto-scroll effect
    useEffect(() => {
      if (terminalRef.current) {
        terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
      }
    }, [output, loading]);

    // Command processing
    const processCommand = async (command) => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let response;
      switch (command.toLowerCase()) {
        case 'help':
          response = generateHelpResponse();
          break;
        case 'start':
          response = generateStartResponse();
          break;
        case 'overview':
          response = generateOverviewResponse();
          break;
        case 'tokenomics':
          response = generateTokenomicsResponse();
          break;
        case 'roadmap':
          response = generateRoadmapResponse();
          break;
        case 'whitepaper':
          response = generateWhitepaperResponse();
          break;
        case 'faq':
          response = generateFaqResponse();
          break;
        case 'presale':
          response = generatePresaleResponse();
          break;
        case 'clear':
          setOutput([]);
          setLoading(false);
          return;
        case 'exit':
          response = { text: "Goodbye! Closing terminal..." };
          break;
        default:
          response = { text: "Command not recognized. Type 'help' for available commands." };
      }
  
      setOutput(prev => [...prev, { type: 'command', content: command }, { type: 'response', content: response }]);
      setLoading(false);
    };

    // Command handlers
    const generateHelpResponse = () => ({

      commands: [
        { command: 'start', description: 'Begin your AI TRADER journey' },
        { command: 'overview', description: 'Project overview' },
        { command: 'tokenomics', description: 'Token distribution and economics' },
        { command: 'roadmap', description: 'Project development timeline' },
        { command: 'whitepaper', description: 'Access the full whitepaper' },
        { command: 'faq', description: 'Frequently asked questions' },
        { command: 'presale', description: 'Information about the AIDER token presale' },
        { command: 'clear', description: 'Clear the terminal screen' },
        { command: 'exit', description: 'Exit the program' }
      ]
    });

    const generateStartResponse = () => ({
      text: "Welcome to AI TRADER. Your decentralized crypto trading journey begins now!"
    });

    const generateOverviewResponse = () => ({
      component: <OverviewCommand />
    });

    const generateTokenomicsResponse = () => ({
      component: <TokenomicsCommand />
    });

    const generateRoadmapResponse = () => ({
      component: <RoadmapCommand />
    });

    const generateWhitepaperResponse = () => ({
      component: <WhitepaperCommand />
    });

    const generateFaqResponse = () => ({
        component: <FaqCommand />
    });

    const generatePresaleResponse = () => ({
        component: <PresaleCommand />
      });

    // Input handling
    const handleInputSubmit = (e) => {
      e.preventDefault();
      if (!inputValue.trim()) return;
      
      processCommand(inputValue);
      setInputValue('');
    };

    // Command click handler
    const handleCommandClick = (command) => {
      processCommand(command);
    };

    // Render output item
    const renderOutputItem = (item) => {
      if (item.type === 'command') {
        return <p>{`> ${item.content}`}</p>;
      }

      return (
        <div>
          {item.content.ascii && (
            <pre className="ascii-art" style={{ whiteSpace: 'pre', fontFamily: 'monospace' }}>
              {item.content.ascii}
            </pre>
          )}
          {item.content.text && (
            <pre style={{ whiteSpace: 'pre', fontFamily: 'monospace' }}>
              {item.content.text}
            </pre>
          )}
          {item.content.commands && (
            <>
              <p>Available commands:</p>
              {item.content.commands.map((cmd, index) => (
                <p key={index}>
                  {'>'}
                  <span 
                    className="clickable" 
                    onClick={() => handleCommandClick(cmd.command)}
                  > {cmd.command}</span> - {cmd.description}
                </p>
              ))}
            </>
          )}
          {item.content.component && (
            <div>{item.content.component}</div>
          )}
        </div>
      );
    };

    return (
      <>
        <canvas ref={canvasRef} className="matrix-bg" />
        <div className="terminal-container">
          <div ref={terminalRef} className="terminal">
            {/* ASCII Logo */}
            <pre className="ascii-art">
{` █████╗ ██╗    ████████╗██████╗  █████╗ ██████╗ ███████╗██████╗ 
██╔══██╗██║    ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
███████║██║       ██║   ██████╔╝███████║██║  ██║█████╗  ██████╔╝
██╔══██║██║       ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝  ██╔══██╗
██║  ██║██║       ██║   ██║  ██║██║  ██║██████╔╝███████╗██║  ██║
╚═╝  ╚═╝╚═╝       ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝`}
            </pre>

            {/* Welcome Message */}
            <div className="section">
              <h2>Welcome to AI TRADER Terminal</h2>
              <p>Available commands:</p>
              <p>
                {'>'}
                <span 
                  className="clickable" 
                  onClick={() => handleCommandClick('help')}
                > help</span> - Display commands
              </p>
              <p>
                {'>'}
                <span 
                  className="clickable" 
                  onClick={() => handleCommandClick('start')}
                > start</span> - Begin journey
              </p>
              <p>
                {'>'}
                <span 
                  className="clickable" 
                  onClick={() => handleCommandClick('clear')}
                > clear</span> - Clear screen
              </p>
            </div>

            {/* Output Area */}
            <div className="output">
              {output.map((item, index) => (
                <div key={index}>{renderOutputItem(item)}</div>
              ))}
              {loading && (
                <p className="loading-animation">Processing</p>
              )}
            </div>

            {/* Input Line */}
            <form onSubmit={handleInputSubmit} className="input-line">
              <input
                type="text"
                className="user-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                autoFocus
              />
              <span className="cursor">█</span>
            </form>
          </div>
        </div>
      </>
    );
};

export default Terminal;
